const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  MX: 'MXN', // Mexican Peso
  CL: 'CLP', // Chilean Peso
  PE: 'PEN', // Peruvian Sol
  ES: 'EUR', // Euro
  CO: 'COP', // Colombian Peso
  US: 'USD', // US Dollar
  AR: 'ARS', // Argentine Peso
  NO: 'NOK', // Norwegian Krone
  FR: 'EUR', // Euro
  GB: 'GBP', // British Pound
  DE: 'EUR', // Euro
};

const formatCurrencyOption = (currency: string, locale = 'es') => {
  try {
    const name = new Intl.DisplayNames([locale], { 
      type: 'currency',
      fallback: 'code'
    }).of(currency);

    const symbol = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol'
    }).format(0).replace(/[\d., ]/g, '');

    return {
      value: currency,
      label: `${symbol} ${name}`
    };
  } catch {
    return {
      value: currency,
      label: currency
    };
  }
};

export const getCurrencyOptions = () => {
  // Handle server-side rendering
  if (typeof window === 'undefined') {
    return getCurrenciesFromMap();
  }

  try {
    // Get user's locale currency
    const userCurrency = getUserCurrency();
    
    // Get the base currencies from the map
    const mapCurrencies = new Set(Object.values(COUNTRY_CURRENCY_MAP));
    
    // Add user's currency if it's not already in the map
    if (!mapCurrencies.has(userCurrency)) {
      mapCurrencies.add(userCurrency);
    }

    // Format all currencies using user's locale
    return Array.from(mapCurrencies)
      .map(currency => formatCurrencyOption(currency, navigator.language))
      .sort((a, b) => a.label.localeCompare(b.label));

  } catch (error) {
    console.warn('Error getting currency options:', error);
    return getCurrenciesFromMap();
  }
};

// For admin use and fallback
export const getCurrenciesFromMap = () => {
  const uniqueCurrencies = Array.from(new Set(Object.values(COUNTRY_CURRENCY_MAP)));
  return uniqueCurrencies
    .map(currency => formatCurrencyOption(currency))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const getUserCurrency = (): string => {
  if (typeof window === 'undefined') return 'EUR';

  try {
    const formatter = new Intl.NumberFormat(navigator.language);
    const parts = formatter.formatToParts(0);
    const currencyPart = parts.find(part => part.type === 'currency');
    return currencyPart?.value || 'EUR';
  } catch {
    return 'EUR';
  }
};

// Export the map for reference
export const getCountryCurrencyMap = () => COUNTRY_CURRENCY_MAP;

export const formatWorkspacePrice = (
  price: number,
  currency: string = 'EUR',
  // Evaluated at CALL time: a bare `navigator.language` here threw on every SSR of the
  // workspace detail page ("navigator is not defined" → root ErrorBoundary). Spanish
  // formatting is the correct server-side default for this site; the client re-renders
  // with the visitor's real locale on hydration.
  locale: string = typeof navigator !== 'undefined' ? navigator.language : 'es-ES'
) => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
};
